import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api, endpoints } from '../apiConfig';
import toast from 'react-hot-toast';
import styles from './ProfileEdit.module.css';
import Resume from '../components/Resume';
import AddressAutocomplete from '../components/AddressAutocomplete';
import AIEnhanceField from '../components/AIEnhanceField';

const ProfileEdit = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    bio: '',
    resume: '',
    artist_statement: '',
    location: '',
    website: '',
    instagram: '',
    facebook: '',
    twitter: '',
    tiktok: ''
  });
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [enhancing, setEnhancing] = useState({
    bio: false,
    artist_statement: false
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.get(endpoints.user.profile);
        const { name, bio, artist_resume, artist_statement, location, website, instagram, facebook, twitter, tiktok } = response.data;
        setFormData({
          name: name || '',
          bio: bio || '',
          resume: artist_resume || '',
          artist_statement: artist_statement || '',
          location: location || '',
          website: website || '',
          instagram: instagram || '',
          facebook: facebook || '',
          twitter: twitter || '',
          tiktok: tiktok || ''
        });
        setLoading(false);
      } catch (error) {
        console.error('Error fetching profile:', error);
        toast.error('Failed to load profile data. Please try refreshing the page.');
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleEnhance = async (section) => {
    try {
      setEnhancing(prev => ({ ...prev, [section]: true }));
      
      const response = await api.post(endpoints.user.enhance, {
        content: formData[section],
        section
      });

      if (response.data?.enhanced) {
        setFormData(prev => ({
          ...prev,
          [section]: response.data.enhanced
        }));
        toast.success(`${section === 'bio' ? 'Bio' : 'Artist Statement'} enhanced successfully!`);
      }
    } catch (error) {
      console.error('Error enhancing content:', error);
      toast.error('Failed to enhance content. Please try again.');
    } finally {
      setEnhancing(prev => ({ ...prev, [section]: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await api.put(endpoints.user.profile, formData);
      
      if (response.status === 200) {
        toast.success('Profile updated successfully');
        // Only navigate if this was a full form submission
        if (e.nativeEvent.submitter?.getAttribute('data-action') === 'save-and-exit') {
          navigate('/dashboard');
        }
      } else {
        throw new Error('Failed to update profile');
      }
    } catch (err) {
      console.error('Profile update error:', err);
      setError(err.message);
      toast.error(err.message || 'Failed to update profile');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleResumeChange = (newResumeData) => {
    try {
      // Ensure we're working with string data
      const resumeString = typeof newResumeData === 'object' ? 
        JSON.stringify(newResumeData) : 
        newResumeData;

      setFormData(prev => ({
        ...prev,
        resume: resumeString
      }));

      // Don't auto-submit on resume change
      setIsDirty(true);
    } catch (error) {
      console.error('Error updating resume data:', error);
      toast.error('Failed to update resume data');
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>Loading your profile...</p>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <div className={styles.container}>
        <header className={styles.header}>
          <h1>Edit Profile</h1>
          <p className={styles.subtitle}>Update your profile information visible to collectors</p>
        </header>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label htmlFor="name">Display Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your display name"
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="bio">Bio</label>
            <AIEnhanceField
              value={formData.bio}
              onChange={(value) => setFormData(prev => ({ ...prev, bio: value }))}
              section="bio"
              placeholder="Tell collectors about yourself, your background, and your artistic journey..."
            />
            <span className={styles.hint}>This bio will appear on your public profile</span>
          </div>

          <div className={styles.formGroup}>
            <label>Resume</label>
            <Resume
              value={formData.resume || ''}
              onChange={handleResumeChange}
            />
            <span className={styles.hint}>Your resume helps establish your professional background</span>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="artist_statement">Artist Statement</label>
            <AIEnhanceField
              value={formData.artist_statement}
              onChange={(value) => setFormData(prev => ({ ...prev, artist_statement: value }))}
              section="artist_statement"
              placeholder="Share your artistic vision, inspiration, and creative process..."
            />
            <span className={styles.hint}>Your artist statement helps collectors understand your work</span>
          </div>

          <div className={styles.locationField}>
            <label htmlFor="location">Location</label>
            <AddressAutocomplete
              value={formData.location}
              onChange={(location) => setFormData({ ...formData, location })}
              placeholder="Enter your location"
            />
          </div>

          <div className={styles.socialLinks}>
            <h3>Social Media Links</h3>
            <div className={styles.socialLinksGrid}>
              <div className={styles.socialLinkField}>
                <label htmlFor="website">Website</label>
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={formData.website || ''}
                  onChange={handleInputChange}
                  placeholder="https://your-website.com"
                />
              </div>
              <div className={styles.socialLinkField}>
                <label htmlFor="instagram">Instagram</label>
                <input
                  type="url"
                  id="instagram"
                  name="instagram"
                  value={formData.instagram || ''}
                  onChange={handleInputChange}
                  placeholder="https://instagram.com/your-handle"
                />
              </div>
              <div className={styles.socialLinkField}>
                <label htmlFor="facebook">Facebook</label>
                <input
                  type="url"
                  id="facebook"
                  name="facebook"
                  value={formData.facebook || ''}
                  onChange={handleInputChange}
                  placeholder="https://facebook.com/your-page"
                />
              </div>
              <div className={styles.socialLinkField}>
                <label htmlFor="twitter">Twitter</label>
                <input
                  type="url"
                  id="twitter"
                  name="twitter"
                  value={formData.twitter || ''}
                  onChange={handleInputChange}
                  placeholder="https://twitter.com/your-handle"
                />
              </div>
              <div className={styles.socialLinkField}>
                <label htmlFor="tiktok">TikTok</label>
                <input
                  type="url"
                  id="tiktok"
                  name="tiktok"
                  value={formData.tiktok || ''}
                  onChange={handleInputChange}
                  placeholder="https://tiktok.com/@your-handle"
                />
              </div>
            </div>
          </div>

          <div className={styles.formActions}>
            <button type="button" className={styles.cancelButton} onClick={() => navigate('/dashboard')}>
              Cancel
            </button>
            <button
              type="submit"
              data-action="save-and-exit"
              className={`${styles.submitButton} ${isDirty ? styles.dirty : ''}`}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : isDirty ? 'Save Changes' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfileEdit; 